import { getLang, setLang, type Lang } from '@bia-auction/shared-i18n';
import { useSyncExternalStore } from 'react';

/**
 * Reactive language state for the bidder app (M7). `@bia-auction/shared-i18n` holds the
 * active language in a module-level variable, which is not reactive — so this thin store adds
 * a subscription (for the switcher to re-render the tree) and persists the choice to
 * localStorage. Turkish is the default; the selection is restored on boot via {@link initLang}.
 */

const LANG_KEY = 'bia.bidder.lang';
const listeners = new Set<() => void>();

/** Restore the persisted language (call once before render). */
export function initLang(): void {
  const saved = localStorage.getItem(LANG_KEY);
  if (saved === 'tr' || saved === 'en') {
    setLang(saved);
    document.documentElement.lang = saved;
  }
}

/**
 * Whether the visitor has actually *chosen* a language, as opposed to running on the Turkish
 * default. Lets the shell skip its login-time flush to the server when there is no choice to
 * flush — without this, every boot PUTs the default back and asks the server to re-decide
 * something nobody changed.
 */
export function hasSavedLang(): boolean {
  const saved = localStorage.getItem(LANG_KEY);
  return saved === 'tr' || saved === 'en';
}

/** Switch language, persist it, and notify subscribers so the tree re-renders. */
export function changeLang(lang: Lang): void {
  setLang(lang);
  localStorage.setItem(LANG_KEY, lang);
  document.documentElement.lang = lang;
  for (const l of listeners) l();
}

/** Reactive current language (re-renders on {@link changeLang}). */
export function useLang(): Lang {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => getLang(),
    () => getLang(),
  );
}
